import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, limit, onSnapshot } from 'firebase/firestore';
import { authService } from '../../services/authService';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icons } from '../../utils/icons';

function tiempoRelativo(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const horas = Math.round(min / 60);
  if (horas < 24) return `hace ${horas}h`;
  return `hace ${Math.round(horas / 24)}d`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (!user?.email) return;
    // Sin orderBy en la query: `where(userId) + orderBy(createdAt)` exige un
    // índice compuesto que no existe en producción. Ordenamos en cliente — el
    // histórico de notificaciones por usuario es pequeño. (Cuando se despliegue
    // firestore.indexes.json se puede volver al orden en servidor.)
    const q = query(
      collection(db, 'notificaciones'),
      where('userId', '==', user.email),
      limit(150)
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      rows.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
      setNotifs(rows.slice(0, 30));
    }, (err) => console.error('[onSnapshot notificaciones]', err));
    return () => unsub();
  }, [user?.email]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const noLeidas = notifs.filter(n => !n.leida).length;

  const marcarTodasLeidas = async () => {
    if (noLeidas === 0) return;
    try {
      await fetch('/.netlify/functions/marcarNotificacionLeida', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ userId: user.email }),
      });
    } catch (e) { console.error('[marcarNotificacionLeida]', e.message); }
  };

  const handleToggle = () => {
    const abriendo = !open;
    setOpen(abriendo);
    if (abriendo) marcarTodasLeidas();
  };

  if (!user?.email) return null;

  return (
    <div className="relative" ref={ref}>
      <button onClick={handleToggle} className="task-button relative" title="Notificaciones">
        <Icons.Bell className="w-5 h-5" />
        {noLeidas > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {noLeidas > 9 ? '9+' : noLeidas}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 z-50">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-bold text-gray-800">Notificaciones</p>
          </div>
          {notifs.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">Sin notificaciones</p>
          )}
          <div className="divide-y divide-gray-50">
            {notifs.map(n => (
              <div key={n.id} className={`px-4 py-3 text-sm ${n.leida ? '' : 'bg-red-50/40'}`}>
                <p className="text-gray-700">{n.mensaje}</p>
                <p className="text-xs text-gray-400 mt-0.5">{tiempoRelativo(n.createdAt)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
